import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getFirestore, collection, doc, setDoc, deleteDoc, onSnapshot, serverTimestamp, Timestamp,
} from 'firebase/firestore'
import { useAuth } from '@/hooks/useAuth'
import { isDevAuthBypass } from '@/lib/firebase'

export interface PresenceUser {
  userId: string
  currentSlide: number
  displayName: string
  photoURL: string | null
  lastSeen: Date | null
}

const HEARTBEAT_INTERVAL = 30_000
const STALE_THRESHOLD = 5 * 60_000

export function usePresence(deckSlug: string, slideIndex: number) {
  const { user } = useAuth()
  const [viewers, setViewers] = useState<PresenceUser[]>([])
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const updatePresence = useCallback(async (slide: number) => {
    if (isDevAuthBypass) return
    if (!user) return
    const db = getFirestore()
    const presenceRef = doc(db, 'decks', deckSlug, 'presence', user.uid)
    await setDoc(presenceRef, {
      currentSlide: slide,
      displayName: user.displayName || 'Anonymous',
      photoURL: user.photoURL || null,
      lastSeen: serverTimestamp(),
    })
  }, [user, deckSlug])

  useEffect(() => { updatePresence(slideIndex) }, [slideIndex, updatePresence])

  useEffect(() => {
    heartbeatRef.current = setInterval(() => { updatePresence(slideIndex) }, HEARTBEAT_INTERVAL)
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current) }
  }, [slideIndex, updatePresence])

  useEffect(() => {
    return () => {
      if (isDevAuthBypass) return
      if (!user) return
      const db = getFirestore()
      deleteDoc(doc(db, 'decks', deckSlug, 'presence', user.uid)).catch(() => {})
    }
  }, [user, deckSlug])

  useEffect(() => {
    if (isDevAuthBypass) return
    const db = getFirestore()
    const presenceCol = collection(db, 'decks', deckSlug, 'presence')
    const unsub = onSnapshot(presenceCol, snap => {
      const now = Date.now()
      const users: PresenceUser[] = []
      snap.docs.forEach(d => {
        const data = d.data()
        const lastSeen = data.lastSeen instanceof Timestamp ? data.lastSeen.toDate() : null
        if (lastSeen && (now - lastSeen.getTime()) > STALE_THRESHOLD) return
        users.push({
          userId: d.id,
          currentSlide: data.currentSlide ?? 0,
          displayName: data.displayName ?? 'Anonymous',
          photoURL: data.photoURL ?? null,
          lastSeen,
        })
      })
      setViewers(users)
    })
    return unsub
  }, [deckSlug])

  return { viewers }
}
